import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import interact from 'interactjs';
import { Drag, DraggableDirective } from '../../directives/draggable.directive';
import { AwallComponentEvent } from '../../models/awallComponentEvent';
import { ComponentEvent } from '../../models/enums/componentEvent';
import {
  CommunicationService,
  Listener,
} from '../../services/communication.service';
import { DynamicElementService } from '../../services/dynamicElement.service';
import { ToolbarSettingsService } from '../../services/toolbar-settings.service';
import { WidgetDock } from '../../util/WidgetDock';
import {
  DoubleClickDetector,
  DoubleClickHandler,
} from '../../util/double-click-detector';
import { DraggedWidgetHolder } from '../widget-wrapper/DraggedWidgetHolder';
import { WidgetWrapperComponent } from '../widget-wrapper/widget-wrapper.component';
import { WidgetWrapperState } from '../widget-wrapper/widgetWrapperState';
import { FilterWidgetComponent } from '../widgets/filter-widget/filter-widget.component';
import { SettingsWidgetComponent } from '../widgets/settings-widget/settings-widget.component';
import { StickerWidgetComponent } from '../widgets/sticker-widget/sticker-widget.component';
import { VtrWidgetComponent } from '../widgets/vtr-widget/vtr-widget.component';
import { WhiteboardWidgetComponent } from '../widgets/whiteboard-widget/whiteboard-widget.component';
import { SwipeDetector } from './SwipeDetector';
import { ToolbarWidgetDock } from './ToolbarWidgetDock';
import { ToolbarWidgetType } from './ToolbarWidgetType';
import { TOOLBAR_COMPONENT_ID } from './const';
import { ChatWidgetComponent } from '../widgets/chat-widget/chat-widget.component';
import { ChannelService } from '../../services/channel.service';
import { filter, Subscription, switchMap, of } from 'rxjs';
import { SettingsEventType } from '../../models/enums/settingsEventType';
import { SettingsService } from '../../services/settings.service';
import { OnlineuserService } from '../../services/onlineuser.service';
import { merge } from 'rxjs';

const TOOLBAR_HEADER_WIDTH = 3;
const TOOLBAR_HEADER_HEIGHT = 2.5;
const TOOLBAR_BODY_WIDTH = 6;
const TOOLBAR_BODY_HEIGHT = 4;

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss', './toolbar-tool.component.scss'],
  }
)

export class ToolbarComponent
implements
    OnInit,
    OnDestroy,
    AfterViewInit,
    DoubleClickHandler,
    Listener<AwallComponentEvent> {
  public static readonly componentId = TOOLBAR_COMPONENT_ID;

  @ViewChild('root') rootContainer: ElementRef<HTMLDivElement>;
  @ViewChild(DraggableDirective) draggableDirective: DraggableDirective;
  @ViewChild('widgetWrapperInsertionPoint', {
    read: ViewContainerRef,
    static: true,
    }
  )
  widgetWrapperInsertionPoint: ViewContainerRef;
  @ViewChild('widgetWrapperInsertionPointFloating', {
    read: ViewContainerRef,
    static: true,
    })
  widgetWrapperInsertionPointFloating: ViewContainerRef;
  public dropContainerSelector = 'body';

  // This map contains the Componentref for each WidgetWrapper with the key of the corresponding widget-name
  public widgetWrapperComponentRefs = new Map<
  string,
  ComponentRef<WidgetWrapperComponent>
  >();

  private widgetDock: WidgetDock;
  private doubleClickDetector: DoubleClickDetector | null = null;
  private swipeDetector: SwipeDetector;
  private counters = [{
    name: 'chat',
    count: 0
  }];
  private channelCounterSubscription: Subscription;
  private channelCounterUpdateSubscription: Subscription;

  public widgetTypes: ToolbarWidgetType[] = [
    new ToolbarWidgetType(
      SettingsWidgetComponent,
      SettingsWidgetComponent.widgetName,
      { provider: 'app', name: 'settings' }
    ),
    new ToolbarWidgetType(ChatWidgetComponent, ChatWidgetComponent.widgetName, {
      provider: 'app',
      name: 'chat',
    }),
    new ToolbarWidgetType(VtrWidgetComponent, VtrWidgetComponent.widgetName, {
      provider: 'app',
      name: 'camera',
    }),
    new ToolbarWidgetType(
      WhiteboardWidgetComponent,
      WhiteboardWidgetComponent.widgetName,
      { provider: 'app', name: 'draw' }
    ),
    new ToolbarWidgetType(
      StickerWidgetComponent,
      StickerWidgetComponent.widgetName,
      { provider: 'app', name: 'sticker' }
    ),
    new ToolbarWidgetType(
      FilterWidgetComponent,
      FilterWidgetComponent.widgetName,
      { provider: 'app', name: 'filter' }
    ),
  ];

  get widgetOrder() {
    return this.widgetTypes.map(({ name }) => name);
  }

  public isTop = false;
  public isLeft = false;
  public isDragging = false;

  private _docked: false | 'top' | 'right' | 'bottom' | 'left' = false;
  public get docked() {
    return this._docked;
  }
  public set docked(docked: false | 'top' | 'right' | 'bottom' | 'left') {
    this._docked = docked;
    this.settingsService.setJSONSetting('docked', docked);
  }

  public _position: { x: number; y: number };
  public get position() {
    return this._position;
  }
  public set position(position: { x: number; y: number }) {
    this._position = position;
    this.settingsService.setJSONSetting('position', position);
  }

  public _orientation: 'vertical' | 'horizontal' = 'vertical';
  public get orientation(): 'vertical' | 'horizontal' {
    return this._orientation;
  }
  public set orientation(orientation: 'vertical' | 'horizontal') {
    this._orientation = orientation;
    this.settingsService.setJSONSetting('orientation', orientation);
  }
  public get isHorizontal() {
    return this.orientation === 'horizontal';
  }
  public get isVertical() {
    return this.orientation === 'vertical';
  }

  constructor(
    private settingsService: ToolbarSettingsService,
    private communicationService: CommunicationService,
    private dynamicElementService: DynamicElementService,
    private channelService: ChannelService,
    private globalSettingsService: SettingsService,
    private onlineUserService: OnlineuserService
  ) { }

  public ngOnInit(): void {
    const body = document.body;

    this.doubleClickDetector = new DoubleClickDetector(body, this);
    this.swipeDetector = new SwipeDetector((direction) => {
      this.docked = direction;

      if (direction === 'left' || direction === 'right') {
        let x = -this.rootContainer.nativeElement.clientWidth + 20;
        const y =
          document.body.clientHeight / 2 -
          this.rootContainer.nativeElement.clientHeight / 2;

        if (direction === 'right') {
          x = document.body.clientWidth - 20;
        }

        this.draggableDirective.setPosition({ x, y }, false);
      }
    });

    const position = this.settingsService.getJSONSetting('position');
    this.orientation =
      this.settingsService.getJSONSetting('orientation') ?? 'vertical';
    this.docked = this.settingsService.getJSONSetting('docked') ?? false;

    if (position) {
      this.position = position;
    }

    this.communicationService.registerComponent(
      ToolbarComponent.componentId,
      this
    );


    this.channelCounterSubscription = merge(
      this.globalSettingsService.getUpdateObservable().pipe(
        filter(evt => evt === SettingsEventType.PROJECT_CHANGED)
      ),
      this.channelService.messageEventUpdater.pipe(
        filter(evt => evt === 'message_read')
      )
    ).pipe(
      switchMap(() => {
        const projectKey = this.globalSettingsService.getProjectName();
        const me = this.onlineUserService.getMe();
        if (!me) {
          return of(0);
        }
        const userId = me.id;
        return this.channelService.getUnreadMessages(projectKey, userId);
      })
    ).subscribe({
      next: (res) => {
        const index = this.counters.findIndex(entity => entity.name === 'chat');
        if (index > -1) {
          this.counters[index].count = res;
        }
      }
    });
  }

  public ngAfterViewInit(): void {
    if (this.position) {
      this.draggableDirective?.setPosition(this.position, false);
    }

    this.setupWidgetDock();
    this.setupBodyWidgetDropzone();
  }

  public ngOnDestroy(): void {
    this.doubleClickDetector?.destroy();
    this.channelCounterSubscription?.unsubscribe();
    this.channelCounterUpdateSubscription?.unsubscribe();
  }

  public onCommunicationEvent(event: AwallComponentEvent): void {
    if (!event) {
      return;
    }

    switch (event.name) {
      case ComponentEvent.TOOLBAR_INSERT_WRAPPER: {
        const widgetName = <string>event.value['widgetName'];
        const wrapperComponentRef = <ComponentRef<WidgetWrapperComponent>>(
          event.value['wrapperComponentRef']
        );
        this.widgetDock.attachWidget(
          widgetName,
          wrapperComponentRef,
          this.widgetOrder
        );
        break;
      }
      case ComponentEvent.TOOLBAR_REMOVE_WRAPPER: {
        const widgetName = <string>event.value['widgetName'];
        this.widgetDock.detachWidget(widgetName);
        break;
      }
      case ComponentEvent.TOOLBAR_CLOSE_WRAPPER: {
        const widgetName = <string>event.value['widgetName'];
        const widgetType = this.widgetTypes.find(
          (type) => type.name === widgetName
        );

        widgetType?.deactivate();

        this.widgetDock.deactivateWidget(widgetType);

        break;
      }
    }
  }

  private setupWidgetDock() {
    this.widgetDock = new ToolbarWidgetDock(
      this.widgetWrapperInsertionPoint,
      this.widgetWrapperInsertionPointFloating,
      this.dynamicElementService
    );
  }

  /**
   * This sets up the element for the drop container as dropzone for widgetwrappers
   */
  private setupBodyWidgetDropzone() {
    interact(this.dropContainerSelector).dropzone({
      accept: '.toolbar-widget > div.widget__inner > div.widget__header',
      overlap: 0.1,
      ondragenter: () => {
        const draggedWidgetWrapper = DraggedWidgetHolder.draggedWidget;
        if (!draggedWidgetWrapper.isDocked()) {
          return;
        }
        // Set State to 'Undocked Dragging' now that we entered the actionview
        draggedWidgetWrapper.setState(WidgetWrapperState.UNDOCKED_DRAGGING);
      },
    });
  }

  public handleToolToggle(widgetType: ToolbarWidgetType) {
    widgetType.toggleActive();

    if (widgetType.active) {
      this.widgetDock.activateWidget(widgetType);
    } else {
      this.widgetDock.deactivateWidget(widgetType);
    }
  }

  public onDoubleClick(event: MouseEvent | TouchEvent): void {
    const element = this.rootContainer.nativeElement;

    const getCoordinates = () => {
      if (event instanceof TouchEvent) {
        const touch = event.changedTouches.item(0);

        return { x: touch.clientX, y: touch.clientY };
      } else if (event instanceof MouseEvent) {
        return { x: event.clientX, y: event.clientY };
      }

      return null;
    };

    const updatePosition = () => {
      const pointerPosition = getCoordinates();

      if (!pointerPosition) {
        return;
      }

      // Calculate center position
      const halfWidth = element.clientWidth / 2;
      const halfHeight = element.clientHeight / 2;

      // Position at the center of the toolbar
      const centerPosition = {
        x: pointerPosition.x - halfWidth,
        y: pointerPosition.y - halfHeight,
      };

      // Calculate offset position
      // When the pointer is in the left half of the screen the toolbar should be 1rem right of the cursor
      // When the pointer is in the right half of the screen the toolbar should be 1rem left of the cursor
      const isLeft = pointerPosition.x < document.body.clientWidth / 2;
      const rem = parseInt(getComputedStyle(document.body).fontSize, 10);
      const xOffset = halfWidth + rem;

      const offsetPosition = {
        x: centerPosition.x + (isLeft ? xOffset : -xOffset),
        y: centerPosition.y,
      };

      this.draggableDirective.setPosition(offsetPosition);
      this.position = offsetPosition;

      this.isTop = pointerPosition.y < document.body.clientHeight / 2;
      this.isLeft = isLeft;
    };

    updatePosition();

    this.docked &&= false;
  }

  public onDragStart(drag: Drag) {
    this.position = drag.current;
  }

  public onDrag(drag: Drag) {
    this.isLeft = drag.current.x < document.body.clientWidth / 2;
    this.isTop = drag.current.y < document.body.clientHeight / 2;
    this.isDragging ||= true;

    this.swipeDetector.move(drag);
  }

  public onDragged(drag: Drag) {
    if (drag.current) {
      this.isDragging = false;
      const isSwipe = this.swipeDetector.release(drag);

      if (!isSwipe) {
        this.position = {
          x: Math.round(drag.current.x),
          y: Math.round(drag.current.y),
        };
      }
    }
  }

  flip = (_: Event) => {
    if (this.docked) {
      return;
    }

    const isHorizontal = !this.isHorizontal;
    const { left, top } =
      this.rootContainer.nativeElement.getBoundingClientRect();

    const position = { x: left, y: top };

    const fontSizeText = window
      .getComputedStyle(this.rootContainer.nativeElement, null)
      .getPropertyValue('font-size');
    const fontSize = parseFloat(fontSizeText);

    const headerWidth = TOOLBAR_HEADER_WIDTH * fontSize;
    const headerHeight = TOOLBAR_HEADER_HEIGHT * fontSize;
    const bodyWidth = this.widgetTypes.length * TOOLBAR_BODY_WIDTH * fontSize;
    const bodyHeight = this.widgetTypes.length * TOOLBAR_BODY_HEIGHT * fontSize;
    const buttonOffset = fontSize;

    const xOffset = headerWidth + bodyWidth - buttonOffset;
    const yOffset = headerHeight + bodyHeight;

    if (isHorizontal) {
      position.x -= xOffset;
      position.y += yOffset;
      this.orientation = 'horizontal';
    } else {
      position.x += xOffset;
      position.y -= yOffset;
      this.orientation = 'vertical';
    }

    this.position = position;
    this.draggableDirective.setPosition(position, false);
  };

  public undock() {
    this.docked = false;
    this.draggableDirective.setPosition(this.position);
  }

  getToolPosition() {
    if (this.isHorizontal) {
      if (this.isTop) {
        return 'below';
      } else {
        return 'above';
      }
    } else {
      if (this.isLeft) {
        return 'right';
      } else {
        return 'left';
      }
    }
  }

  getCounterByName(iconName: string): number {
    return this.counters.find(entity => entity.name === iconName)?.count || 0;
  }
}
